import { useState, useCallback, useEffect } from 'preact/hooks'
import { assetApi } from '@/api/asset'
import { MarkdownView } from '@/components/common/MarkdownView'
import type { LandingPageLayout, LandingPageContextDto, LandingPageViewDto, ThankYouPageSettingsDto, PageMetaDto, LandingPageContentImageDto } from '@/types/landingPage'
import { PageMetaSection } from './PageMetaSection'
import { PageImagesSection } from './PageImagesSection'
import { FontsSection } from './FontsSection'
import { RichTextBox } from '@/components/common/RichTextBox'
import { LayoutSelector } from './LayoutSelector'
import { DisplaySettings } from './DisplaySettings'
import { FormFieldsPicker } from './FormFieldsPicker'
import { ToggleRow } from '@/components/common/ToggleRow'
import { BRAND_CLASSES } from '@/constants/theme'
import { CopyButton } from '@/components/common/CopyButton'
import { ExternalLink } from 'lucide-preact'
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-preact'

/** KB context fields shown in the UI. Add more entries here to surface additional fields. */
const CONTEXT_FIELDS: Array<{ key: keyof LandingPageContextDto; label: string }> = [
    { key: 'ToneOfVoice', label: 'Tone of Voice' },
    { key: 'BrandGuidelines', label: 'Brand Guidelines' },
    { key: 'VisualNarrative', label: 'Visual Narrative' },
]

function SectionHeader({ children, collapsible, expanded, onToggle }: {
    children: string
    collapsible?: boolean
    expanded?: boolean
    onToggle?: () => void
}) {
    if (!collapsible) {
        return (
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                {children}
            </h3>
        )
    }
    return (
        <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1.5 w-full text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {children}
        </button>
    )
}

interface LandingPageSidebarProps {
    context: LandingPageContextDto | null
    contextLoading?: boolean
    selectedLayout: LandingPageLayout
    onSelectLayout: (layout: LandingPageLayout) => void
    showAgenda: boolean
    showSpeakerBios: boolean
    showCountdown: boolean
    showDuration: boolean
    includeTracking: boolean
    /** Per-LP toggle for the company's custom tracking script (e.g. GTM). */
    includeCustomScript: boolean
    onToggleSetting: (field: 'ShowAgenda' | 'ShowSpeakerBios' | 'ShowCountdown' | 'ShowDuration' | 'IncludeTracking' | 'IncludeCustomScript', value: boolean) => void
    /** Currently selected canonical FormFieldCatalog keys; null = use defaults. */
    formFields?: string[] | null
    onFormFieldsChange?: (canonicalKeys: string[]) => void
    onContextChange: (field: keyof LandingPageContextDto, value: string | string[]) => void
    disabled?: boolean
    compact?: boolean
    /** When true, all fields are display-only (post-generation view) */
    readOnly?: boolean
    thankYouSettings?: ThankYouPageSettingsDto | null
    onThankYouSettingsChange?: (field: keyof ThankYouPageSettingsDto, value: string) => void
    /** Per-page meta override (OG image, favicon, description). Empty = fall back to KB. */
    pageMeta?: PageMetaDto | null
    /** Called when the user edits a PageMeta override value. */
    onPageMetaChange?: (next: PageMetaDto) => void
    /** Called after a successful meta asset upload with the full updated landing page. */
    onLandingPageUpdate?: (updated: LandingPageViewDto) => void
    /** Workspace ID for meta asset uploads. */
    workspaceId?: string
    /** Web ID for meta asset uploads. */
    webId?: string
    /** User-supplied content images (ORB-413). Empty list when none yet. */
    contentImages?: LandingPageContentImageDto[]
    /** Called when content images are added/removed or a note is edited. */
    onContentImagesChange?: (next: LandingPageContentImageDto[]) => void
    /** Web type string — used to conditionally show event-specific settings. */
    webType?: string
    /** Current effective heading/body font families (per-page override or company brand font). */
    headingFont?: string
    bodyFont?: string
    /** Applies a font choice (heading, body). Null clears a role to the system fallback. */
    onApplyFont?: (heading: string | null, body: string | null) => void | Promise<void>
    /** Post-generation: ask Webster to tune type scale/spacing for the current fonts. */
    onRefineFont?: () => void
}

/** Editable list of URL strings with add/remove */
function ReferenceUrlList({ urls, onChange, disabled }: {
    urls: string[]
    onChange: (urls: string[]) => void
    disabled?: boolean
}) {
    const [newUrl, setNewUrl] = useState('')

    const handleAdd = () => {
        const trimmed = newUrl.trim()
        if (!trimmed) return
        onChange([...urls, trimmed])
        setNewUrl('')
    }

    const handleRemove = (index: number) => {
        onChange(urls.filter((_, i) => i !== index))
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleAdd()
        }
    }

    return (
        <div className="space-y-2">
            {urls.map((url, i) => (
                <div key={i} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate" title={url}>
                        {url}
                    </span>
                    <button
                        type="button"
                        onClick={() => handleRemove(i)}
                        disabled={disabled}
                        className="p-1 rounded text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        title="Remove URL"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ))}
            <div className="flex items-center gap-2">
                <input
                    type="url"
                    value={newUrl}
                    onInput={(e) => setNewUrl((e.target as HTMLInputElement).value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder="https://..."
                    className={`flex-1 text-sm px-3 py-1.5 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL_BORDER} ${BRAND_CLASSES.INPUT.NORMAL_BG} text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50`}
                />
                <button
                    type="button"
                    onClick={handleAdd}
                    disabled={disabled || !newUrl.trim()}
                    className="p-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Add URL"
                >
                    <Plus className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    )
}

function ThankYouFields({ settings, onChange, disabled, readOnly }: {
    settings: Partial<ThankYouPageSettingsDto>
    onChange?: (field: keyof ThankYouPageSettingsDto, value: string) => void
    disabled?: boolean
    readOnly?: boolean
}) {
    const inputClass = `w-full text-sm px-3 py-2 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL_BORDER} ${BRAND_CLASSES.INPUT.NORMAL_BG} text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed`

    // Field length caps loaded from the server's FieldLimits catalog so the
    // form maxLength matches the API validator exactly (no drift).
    const [maxLengths, setMaxLengths] = useState<Record<string, number | undefined>>({})

    useEffect(() => {
        let cancelled = false
        assetApi.getFieldLimits('ThankYou').then(res => {
            if (cancelled) return
            if (res.data) {
                const next: Record<string, number | undefined> = {}
                for (const f of res.data.Fields) next[f.FieldKey] = f.MaxLength
                setMaxLengths(next)
            }
        })
        return () => { cancelled = true }
    }, [])

    const fields: Array<{
        key: keyof ThankYouPageSettingsDto
        label: string
        placeholder: string
        type?: 'text' | 'url' | 'textarea'
    }> = [
        { key: 'YouTubeUrl', label: 'YouTube Video URL', placeholder: 'https://youtube.com/watch?v=...' },
        { key: 'CustomMessage', label: 'Custom Message', placeholder: 'Enter a custom thank you message...', type: 'textarea' },
        { key: 'CtaLabel', label: 'CTA Button Label', placeholder: 'e.g. "Visit Our Website"' },
        { key: 'CtaUrl', label: 'CTA Button URL', placeholder: 'https://...', type: 'url' },
    ]

    return (
        <div className="space-y-4">
            {fields.map(({ key, label, placeholder, type }) => {
                const value = settings[key] ?? ''
                if (readOnly && !value) return null
                const maxLength = maxLengths[key]
                const overLimit = maxLength != null && value.length > maxLength
                return (
                    <div key={key}>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                            {label}
                        </label>
                        {readOnly ? (
                            <p className="text-sm text-slate-700 dark:text-slate-300">{value || '—'}</p>
                        ) : type === 'textarea' ? (
                            <textarea
                                value={value}
                                onInput={(e) => onChange?.(key, (e.target as HTMLTextAreaElement).value)}
                                disabled={disabled}
                                placeholder={placeholder}
                                rows={3}
                                maxLength={maxLength}
                                className={`${inputClass} resize-y${overLimit ? ' border-red-500 dark:border-red-500' : ''}`}
                            />
                        ) : (
                            <input
                                type={type ?? 'text'}
                                value={value}
                                onInput={(e) => onChange?.(key, (e.target as HTMLInputElement).value)}
                                disabled={disabled}
                                placeholder={placeholder}
                                maxLength={maxLength}
                                className={`${inputClass}${overLimit ? ' border-red-500 dark:border-red-500' : ''}`}
                            />
                        )}
                        {!readOnly && maxLength != null && (
                            <div className={`text-xs mt-1 text-right ${overLimit ? 'text-red-500 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                {value.length} / {maxLength}
                            </div>
                        )}
                    </div>
                )
            })}
            {settings.DownloadUrl && (
                <div>
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                        Download File URL <span className="text-slate-400 dark:text-slate-500 font-normal">(auto-populated)</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <a href={settings.DownloadUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate flex items-center gap-1">
                            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">{settings.DownloadUrl}</span>
                        </a>
                        <CopyButton text={settings.DownloadUrl} label="Download URL" />
                    </div>
                </div>
            )}
        </div>
    )
}

export function LandingPageSidebar({
    context,
    contextLoading,
    selectedLayout,
    onSelectLayout,
    showAgenda,
    showSpeakerBios,
    showCountdown,
    showDuration,
    includeTracking,
    includeCustomScript,
    onToggleSetting,
    formFields,
    onFormFieldsChange,
    onContextChange,
    disabled,
    compact,
    readOnly,
    thankYouSettings,
    onThankYouSettingsChange,
    pageMeta,
    onPageMetaChange,
    onLandingPageUpdate,
    workspaceId,
    webId,
    headingFont,
    bodyFont,
    onApplyFont,
    onRefineFont,
    contentImages,
    onContentImagesChange,
    webType,
}: LandingPageSidebarProps) {
    const isOnlineEvent = (webType || '').toLowerCase().includes('online')
    const [settingsExpanded, setSettingsExpanded] = useState(!compact)
    const [contextExpanded, setContextExpanded] = useState(true)
    const [thankYouExpanded, setThankYouExpanded] = useState(!compact)
    const [pageMetaExpanded, setPageMetaExpanded] = useState(false)
    const [pageImagesExpanded, setPageImagesExpanded] = useState(false)

    // Field length caps loaded from the server's FieldLimits catalog so the
    // Context inputs (and PageMetaSection) enforce the same maxLength the
    // API validator does — no drift.
    const [landingPageLimits, setLandingPageLimits] = useState<Record<string, number | undefined>>({})

    useEffect(() => {
        let cancelled = false
        assetApi.getFieldLimits('LandingPage').then(res => {
            if (cancelled) return
            if (res.data) {
                const next: Record<string, number | undefined> = {}
                for (const f of res.data.Fields) next[f.FieldKey] = f.MaxLength
                setLandingPageLimits(next)
            }
        })
        return () => { cancelled = true }
    }, [])

    const handleFieldChange = useCallback((key: keyof LandingPageContextDto, value: string | string[]) => {
        onContextChange(key, value)
    }, [onContextChange])

    if (contextLoading) {
        return (
            <div className={`${compact ? '' : 'p-6'} flex items-center justify-center py-12`}>
                <div className="flex flex-col items-center gap-3">
                    <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">Loading context...</span>
                </div>
            </div>
        )
    }

    const brandColorFields: Array<{ field: keyof LandingPageContextDto; label: string }> = [
        { field: 'BrandColorPrimary', label: 'Primary' },
        { field: 'BrandColorSecondary', label: 'Secondary' },
        { field: 'BrandColorAccent', label: 'Accent' },
        { field: 'BrandColorBackground', label: 'Bg' },
        { field: 'BrandColorText', label: 'Text' },
    ]

    return (
        <div className={`space-y-5 ${compact ? 'p-3' : 'p-5'}`}>
            {/* Brand Identity */}
            {context && (
                <>
                    <div>
                        <SectionHeader>Brand Identity</SectionHeader>
                        <div className="space-y-3">
                            {/* Brand images */}
                            {(context.LogoUrl || context.IconUrl || context.BannerUrl) && (
                                <div className="flex items-center gap-3 flex-wrap">
                                    {context.LogoUrl && (
                                        <img
                                            src={context.LogoUrl}
                                            alt="Brand logo"
                                            className="h-10 max-w-[180px] object-contain"
                                        />
                                    )}
                                    {context.IconUrl && (
                                        <img
                                            src={context.IconUrl}
                                            alt="Brand icon"
                                            className="h-10 w-10 object-contain rounded"
                                        />
                                    )}
                                    {context.BannerUrl && (
                                        <img
                                            src={context.BannerUrl}
                                            alt="Brand banner"
                                            className="h-10 max-w-[200px] object-contain rounded"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Editable color pickers */}
                            <div>
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Colors</label>
                                <div className="flex flex-wrap gap-3">
                                    {brandColorFields.map(({ field, label }) => (
                                        <div key={field} className="flex flex-col items-center gap-1">
                                            <label
                                                className="relative w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 shadow-sm overflow-hidden cursor-pointer"
                                                style={{ backgroundColor: (context[field] as string) || '#ffffff' }}
                                                title={`${label}: ${(context[field] as string) || 'not set'}`}
                                            >
                                                <input
                                                    type="color"
                                                    value={(context[field] as string) || '#ffffff'}
                                                    onInput={(e) => handleFieldChange(field, (e.target as HTMLInputElement).value)}
                                                    disabled={disabled || readOnly}
                                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                />
                                            </label>
                                            <span className="text-[10px] text-slate-400 dark:text-slate-500">{label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Brand fonts — editable picker while setting up (create flow);
                                read-only display in the post-generation reference panel
                                (readOnly=true). Post-gen editing lives in the dedicated
                                Typography accordion; the chat "use Poppins" flip also works. */}
                            {readOnly ? (
                                <div>
                                    <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Fonts</label>
                                    <div className="space-y-1">
                                        <div className="flex items-baseline justify-between gap-2 text-sm">
                                            <span className="text-xs text-slate-400 dark:text-slate-500">Heading</span>
                                            <span className="min-w-0 flex-1 text-right truncate text-slate-700 dark:text-slate-200" title={headingFont || 'System default'}>{headingFont || 'System default'}</span>
                                        </div>
                                        <div className="flex items-baseline justify-between gap-2 text-sm">
                                            <span className="text-xs text-slate-400 dark:text-slate-500">Body</span>
                                            <span className="min-w-0 flex-1 text-right truncate text-slate-700 dark:text-slate-200" title={bodyFont || 'System default'}>{bodyFont || 'System default'}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                workspaceId && webId && onApplyFont && (
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Fonts</label>
                                        <FontsSection
                                            workspaceId={workspaceId}
                                            webId={webId}
                                            headingFamily={headingFont}
                                            bodyFamily={bodyFont}
                                            disabled={disabled}
                                            onApply={onApplyFont}
                                            onRefine={onRefineFont}
                                        />
                                    </div>
                                )
                            )}

                            {/* Editable tagline */}
                            <div>
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Tagline</label>
                                <input
                                    type="text"
                                    value={context.BrandTagline ?? ''}
                                    onInput={(e) => handleFieldChange('BrandTagline', (e.target as HTMLInputElement).value)}
                                    disabled={disabled || readOnly}
                                    placeholder="Your brand tagline..."
                                    className={`w-full text-sm px-3 py-1.5 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL_BORDER} ${BRAND_CLASSES.INPUT.NORMAL_BG} text-slate-700 dark:text-slate-200 italic focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50`}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-slate-200 dark:border-slate-700" />
                </>
            )}

            {/* Layout & Display Settings */}
            <div>
                <SectionHeader
                    collapsible={compact}
                    expanded={settingsExpanded}
                    onToggle={() => setSettingsExpanded(!settingsExpanded)}
                >
                    Settings
                </SectionHeader>
                {settingsExpanded && (
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Layout</label>
                            <LayoutSelector
                                selected={selectedLayout}
                                onSelect={onSelectLayout}
                                disabled={disabled || readOnly}
                                compact={compact}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Visitor Tracking</label>
                            <ToggleRow
                                label="Include tracking snippet"
                                checked={includeTracking}
                                onChange={(v) => onToggleSetting('IncludeTracking', v)}
                                disabled={disabled || readOnly}
                            />
                            <ToggleRow
                                label="Include custom tracking script"
                                checked={includeCustomScript}
                                onChange={(v) => onToggleSetting('IncludeCustomScript', v)}
                                disabled={disabled || readOnly}
                            />
                            <p className="text-xs text-slate-400 dark:text-slate-500 -mt-1">
                                e.g. Google Tag Manager
                            </p>
                        </div>
                        {isOnlineEvent && (
                            <div>
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Display Options</label>
                                <DisplaySettings
                                    showAgenda={showAgenda}
                                    showSpeakerBios={showSpeakerBios}
                                    showCountdown={showCountdown}
                                    showDuration={showDuration}
                                    onChange={onToggleSetting}
                                    disabled={disabled || readOnly}
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Registration Form Fields</label>
                            <FormFieldsPicker
                                selected={formFields}
                                onChange={(keys) => onFormFieldsChange?.(keys)}
                                disabled={disabled}
                                readOnly={readOnly}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* KB Context Fields */}
            <div>
                <SectionHeader
                    collapsible={compact}
                    expanded={contextExpanded}
                    onToggle={() => setContextExpanded(!contextExpanded)}
                >
                    Knowledge Base Context
                </SectionHeader>
                {contextExpanded && (
                    <div className="space-y-4">
                        {!context && (
                            <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                                No context data available. Context will be resolved from your Knowledge Base when the page loads.
                            </p>
                        )}
                        {context && (
                            <>
                                {/* Brand URL */}
                                <div>
                                    <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                                        Brand URL
                                    </label>
                                    <input
                                        type="url"
                                        value={context.BrandUrl ?? ''}
                                        onInput={(e) => handleFieldChange('BrandUrl', (e.target as HTMLInputElement).value)}
                                        disabled={disabled || readOnly}
                                        placeholder="https://yourcompany.com"
                                        className={`w-full text-sm px-3 py-2 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL_BORDER} ${BRAND_CLASSES.INPUT.NORMAL_BG} text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed`}
                                    />
                                </div>

                                {/* Reference URLs */}
                                <div>
                                    <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                                        Reference URLs
                                    </label>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">
                                        Additional URLs for design inspiration
                                    </p>
                                    <ReferenceUrlList
                                        urls={context.ReferenceUrls ?? []}
                                        onChange={(urls) => handleFieldChange('ReferenceUrls', urls)}
                                        disabled={disabled || readOnly}
                                    />
                                </div>

                                {/* KB context fields */}
                                {CONTEXT_FIELDS.map(({ key, label }) => {
                                    const value = (context[key] as string) ?? ''
                                    if (!value && readOnly) return null
                                    const limit = landingPageLimits[key]
                                    const overLimit = limit != null && value.length > limit
                                    return (
                                        <div key={key}>
                                            {readOnly ? (
                                                <>
                                                    <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                        {label}
                                                    </h4>
                                                    <MarkdownView className="text-sm text-slate-700 dark:text-slate-300">{value}</MarkdownView>
                                                </>
                                            ) : (
                                                <>
                                                    <RichTextBox
                                                        name={key}
                                                        label={label}
                                                        labelSize="xs"
                                                        value={value}
                                                        onChange={(md) => handleFieldChange(key, md)}
                                                        placeholder={`Enter ${label.toLowerCase()}...`}
                                                        minHeight={compact ? '80px' : '120px'}
                                                        disabled={disabled || false}
                                                        showSourceToggle={!compact}
                                                    />
                                                    {limit != null && (
                                                        <div className={`text-xs mt-1 text-right ${overLimit ? 'text-red-500 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                                            {value.length} / {limit}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Thank You Page Settings */}
            <div>
                <SectionHeader
                    collapsible={compact}
                    expanded={thankYouExpanded}
                    onToggle={() => setThankYouExpanded(!thankYouExpanded)}
                >
                    Thank You Page
                </SectionHeader>
                {thankYouExpanded && (
                    <ThankYouFields
                        settings={thankYouSettings ?? {}}
                        onChange={onThankYouSettingsChange}
                        disabled={disabled}
                    />
                )}
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Page Meta (OG image / favicon / description — deploy-time metadata) */}
            <div>
                <SectionHeader
                    collapsible
                    expanded={pageMetaExpanded}
                    onToggle={() => setPageMetaExpanded(!pageMetaExpanded)}
                >
                    Page Meta
                </SectionHeader>
                {pageMetaExpanded && (
                    <PageMetaSection
                        context={context}
                        pageMeta={pageMeta ?? null}
                        onPageMetaChange={readOnly ? undefined : onPageMetaChange}
                        onLandingPageUpdate={readOnly ? undefined : onLandingPageUpdate}
                        workspaceId={workspaceId}
                        webId={webId}
                        disabled={disabled || readOnly}
                    />
                )}
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Page Images (user-supplied content imagery — ORB-413) */}
            <div>
                <SectionHeader
                    collapsible
                    expanded={pageImagesExpanded}
                    onToggle={() => setPageImagesExpanded(!pageImagesExpanded)}
                >
                    Page Images
                </SectionHeader>
                {pageImagesExpanded && (
                    <PageImagesSection
                        contentImages={contentImages ?? []}
                        onChange={onContentImagesChange ?? (() => {})}
                        workspaceId={workspaceId}
                        webId={webId}
                        disabled={disabled || readOnly}
                    />
                )}
            </div>
        </div>
    )
}
